import React, { useState, useEffect, useMemo } from 'react';
import { Avatar, Image } from 'antd';
import { UserOutlined, LoadingOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { usePhotoRefresh } from '../context/PhotoRefreshContext';
import imageCache from '../utils/imageCache';

/**
 * EmployeeAvatar Component
 * Displays employee profile picture with optional preview functionality and image caching
 * Automatically refreshes when photo is updated
 *
 * @param {string} empId - Employee ID
 * @param {number|string} size - Avatar size (default: 40)
 * @param {object} style - Additional styles for avatar
 * @param {boolean} enablePreview - Enable click-to-preview functionality (default: false)
 * @param {number} photoUpdatedAt - Unix timestamp for cache-busting (optional, from API)
 */
const EmployeeAvatar = ({ empId, size = 40, style = {}, enablePreview = false, photoUpdatedAt = null }) => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [cachedImageUrl, setCachedImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getPhotoTimestamp } = usePhotoRefresh();

  // Memoize the image URL to prevent unnecessary re-calculations
  const imageUrl = useMemo(() => {
    if (!empId) return null;
    const contextTimestamp = getPhotoTimestamp(empId);
    const effectiveTimestamp = contextTimestamp || photoUpdatedAt;
    return api.getEmployeePhoto(empId, null, effectiveTimestamp);
  }, [empId, photoUpdatedAt, getPhotoTimestamp]);

  useEffect(() => {
    if (!imageUrl) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadCachedImage = async () => {
      try {
        setLoading(true);
        const cached = await imageCache.get(imageUrl);

        if (!cancelled) {
          setCachedImageUrl(cached);
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to load cached employee image:', error);
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCachedImage();

    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  if (!empId || loading) {
    return (
      <Avatar
        size={size}
        icon={loading ? <LoadingOutlined spin /> : <UserOutlined />}
        style={style}
      />
    );
  }

  const avatarElement = (
    <Avatar
      size={size}
      src={cachedImageUrl}
      icon={<UserOutlined />}
      style={{
        ...style,
        cursor: enablePreview ? 'pointer' : 'default'
      }}
      onClick={() => enablePreview && setPreviewVisible(true)}
    />
  );

  if (!enablePreview) {
    return avatarElement;
  }

  return (
    <>
      {avatarElement}
      <Image
        preview={{
          visible: previewVisible,
          src: cachedImageUrl,
          onVisibleChange: (visible) => setPreviewVisible(visible),
        }}
        style={{ display: 'none' }}
      />
    </>
  );
};

export default EmployeeAvatar;
